'use client';

import { useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '@/lib/imageCompression';
import { useUploadWorkerAvatar } from '@/components/hooks/useUploadWorkerAvatar';
import AvatarCropDialog from '@/components/pages/dialogs/AvatarCropDialog';

interface Props {
  userId: string;
  nombre: string;
  foto?: string | null;
  sizeClass?: string;
  onChanged?: () => void;
}

function ensureFile(blob: Blob, originalFile: File): File {
  if (blob instanceof File) return blob;
  return new File([blob], originalFile.name.replace(/\.[^/.]+$/, '.jpg'), {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}

const getIniciales = (nombre: string) =>
  (nombre ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || '?';

// Avatar editable para la fila del propio usuario en la pantalla de
// Trabajadores. La subida actualiza el user_metadata del usuario logueado
// (updateUser), por eso solo se usa cuando la fila es uno mismo.
export default function WorkerSelfAvatar({
  userId,
  nombre,
  foto,
  sizeClass = 'h-12 w-12',
  onChanged,
}: Props) {
  const [fotoLocal, setFotoLocal] = useState<string | null | undefined>(foto);
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const { uploadAvatar, removeAvatar, uploading } = useUploadWorkerAvatar({
    userId,
    onUploaded: (url) => {
      setFotoLocal(url);
      onChanged?.();
    },
    onRemoved: () => {
      setFotoLocal(null);
      onChanged?.();
    },
  });

  return (
    <div className={`relative ${sizeClass} shrink-0`}>
      {fotoLocal ? (
        <img
          src={fotoLocal}
          alt={nombre}
          className={`${sizeClass} rounded-full object-cover border border-gray-200 dark:border-white/10`}
        />
      ) : (
        <div
          className={`${sizeClass} rounded-full bg-[#103254]/10 dark:bg-blue-400/10 text-[#103254] dark:text-blue-400 flex items-center justify-center font-bold text-xs`}
        >
          {getIniciales(nombre)}
        </div>
      )}

      {fotoLocal && (
        <button
          type="button"
          onClick={async () => {
            try {
              await removeAvatar();
            } catch {
              toast.error('No se pudo borrar la foto.');
            }
          }}
          className="absolute -top-1 -right-1 bg-red-600 hover:bg-red-700 p-1 rounded-full shadow-md transition-colors"
          title="Eliminar foto"
        >
          <Trash2 className="w-3.5 h-3.5 text-white" />
        </button>
      )}

      <label
        htmlFor={`self-foto-${userId}`}
        className="absolute -bottom-1 -right-1 bg-[#103254] p-1 rounded-full cursor-pointer shadow-md hover:bg-[#0d2742] transition-colors"
        title="Cambiar foto"
      >
        <Upload className="w-3.5 h-3.5 text-white" />
        <input
          id={`self-foto-${userId}`}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setRawFile(file);
            setCropOpen(true);
            e.target.value = '';
          }}
        />
      </label>

      {rawFile && (
        <AvatarCropDialog
          open={cropOpen}
          file={rawFile}
          onCancel={() => {
            setCropOpen(false);
            setRawFile(null);
          }}
          onSave={async (croppedFile) => {
            try {
              const blob = await compressImage(croppedFile, {
                maxSizeMB: 0.3,
                maxWidthOrHeight: 512,
              });
              const dt = new DataTransfer();
              dt.items.add(ensureFile(blob, croppedFile));
              await uploadAvatar(dt.files);
              setCropOpen(false);
              setRawFile(null);
            } catch (err) {
              console.error(err);
              toast.error('No se pudo procesar el avatar.');
            }
          }}
        />
      )}
    </div>
  );
}